import { LumiDB } from "@lumidb/lumidb";
import * as THREE from "three";
import { Annotations } from "./annotations";
import { App } from "./app";
import { Auth } from "./auth";
import { EarthControls } from "./earth-controls";
import { Journal } from "./history";
import { LOD } from "./lod";
import { Prefs } from "./prefs";
import { Project } from "./project";
import { Terrain } from "./terrain";
import { setArray, setRecord } from "./utils";
import { Viewer } from "./viewer";

// 16/9
const WIDTH = 512;
const HEIGHT = 288;
const LOAD_TIMEOUT_MS = 20_000;
const POLL_INTERVAL_MS = 200;

// 1x1 transparent png
const EMPTY_THUMBNAIL = "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNgAAIAAAUAAen63NgAAAAASUVORK5CYII=";

const EMPTY_THUMBNAIL_HASH = "empty";

export class Thumbnail {
    private readonly lumidb: LumiDB;
    private readonly auth: Auth;
    private readonly project: Project;

    private readonly app: App;
    private readonly viewer: Viewer;
    private readonly prefs: Prefs;
    private readonly lod: LOD;
    private readonly terrain: Terrain;
    private readonly annotations: Annotations;
    private readonly controls: EarthControls;
    private readonly canvas: HTMLCanvasElement;

    private generation = 0;

    constructor(lumidb: LumiDB, auth: Auth, project: Project) {
        this.lumidb = lumidb;
        this.auth = auth;
        this.project = project;

        // Thumbnails set up their own app to avoid coupling
        this.app = new App(this.lumidb, this.auth);
        this.viewer = new Viewer(this.app);
        this.prefs = new Prefs();
        this.lod = new LOD(this.viewer, this.app, this.prefs, this.lumidb, this.project);
        this.terrain = new Terrain(this.viewer, this.app, this.project, this.auth, "light");

        this.annotations = new Annotations(
            this.viewer,
            this.prefs,
            new Journal(),
            this.app,
            this.lod,
            this.project,
            this.terrain,
        );

        this.controls = new EarthControls(
            this.app,
            this.viewer,
            this.lod,
            this.terrain,
            this.project,
            this.annotations,
        );

        this.canvas = document.createElement("canvas");
        this.canvas.width = WIDTH;
        this.canvas.height = HEIGHT;
        this.viewer.init(this.canvas);
        this.viewer.setSize(WIDTH, HEIGHT);
        this.viewer.backgroundColor = new THREE.Color("#808080");
    }

    public async capture() {
        const p = this.project.get();

        // Only editors can persist a thumbnail, so skip the whole render for viewers.
        if (!this.project.canEdit(p.project_id)) {
            return;
        }

        const generation = ++this.generation;

        try {
            const items = this.project.sceneDatasets(p.project_id);
            setRecord(this.app.state.datasets, items);

            const datasetIds = Object.values(items)
                .filter((item) => item.kind === "pointcloud")
                .map((item) => item.id);

            const rasterIds = Object.values(items)
                .filter((item) => item.kind === "raster")
                .map((item) => item.id);

            const vectorIds = Object.values(items)
                .filter((item) => item.kind === "vector")
                .map((item) => item.id);

            setArray(this.app.state.visible, [...datasetIds, ...rasterIds, ...vectorIds]);

            // Nothing to render: replace stale thumbnail with the empty sentinel
            if (datasetIds.length === 0 && rasterIds.length === 0 && vectorIds.length === 0) {
                if (p.thumbnail_hash !== EMPTY_THUMBNAIL_HASH) {
                    await this.project.update(p.project_id, {
                        thumbnail: EMPTY_THUMBNAIL,
                        thumbnail_hash: EMPTY_THUMBNAIL_HASH,
                    });
                    p.thumbnail = EMPTY_THUMBNAIL;
                    p.thumbnail_hash = EMPTY_THUMBNAIL_HASH;
                }
                return EMPTY_THUMBNAIL;
            }

            const thumbnailHash = await this.getHash(datasetIds, rasterIds, vectorIds);
            if (generation !== this.generation) return;
            if (p.thumbnail && thumbnailHash === p.thumbnail_hash) {
                return p.thumbnail;
            }

            await Promise.allSettled(vectorIds.map((vectorId) => this.app.loadDatasetMeta(vectorId)));
            if (generation !== this.generation) return;

            this.controls.focus();

            await this.terrain.init();
            if (generation !== this.generation) return;

            await this.waitUntilLoaded(generation);
            if (generation !== this.generation) return;

            this.viewer.updateNow();

            const hasPoints = this.lod.state.loaded > 0;
            const hasRasters = this.terrain.state.stats.loaded > 0;
            const hasFeatures = this.annotations.hasFeatures();
            const rendered = hasPoints || hasRasters || hasFeatures;
            if (!rendered) return;

            const blob: Blob = await new Promise((resolve, reject) => {
                return this.canvas.toBlob((b) => {
                    if (b) {
                        resolve(b);
                    } else {
                        reject(new Error("Failed to create canvas blob"));
                    }
                }, "image/png");
            });

            if (generation !== this.generation) return;

            const bytes = new Uint8Array(await blob.arrayBuffer());

            let bin = "";
            for (let i = 0; i < bytes.length; i++) {
                bin += String.fromCharCode(bytes[i]);
            }

            const base64 = btoa(bin);
            await this.project.update(p.project_id, { thumbnail: base64, thumbnail_hash: thumbnailHash });
            p.thumbnail = base64;
            p.thumbnail_hash = thumbnailHash;
            return base64;
        } catch (err) {
            console.warn("Failed to capture project thumbnail", err);
        } finally {
            // Only the latest capture cleans up, so a superseded one doesn't unload the
            // tiles the newer capture is still streaming in.
            if (generation === this.generation) {
                this.lod.unloadTables();
                this.terrain.reset();
                this.annotations.reset();
            }
        }
    }

    // Re-select terrain, point, and vector tiles for the thumbnail camera and pump their loads,
    // polling until all loaders drain or the time budget runs out.
    private async waitUntilLoaded(generation: number) {
        const start = performance.now();

        while (performance.now() - start < LOAD_TIMEOUT_MS) {
            if (generation !== this.generation) return;

            this.terrain.eval();
            this.lod.eval();
            this.annotations.eval();

            const terrainIdle = this.terrain.isLoading() === false;
            const lodIdle = this.lod.isLoading() === false;
            const annotationsIdle = this.annotations.isLoading() === false;

            if (terrainIdle && lodIdle && annotationsIdle) {
                return;
            }

            await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        }
    }

    private async getHash(datasetIds: string[], rasterIds: string[], vectorIds: string[]): Promise<string> {
        const input = [
            [...datasetIds].sort().join(","),
            [...rasterIds].sort().join(","),
            [...vectorIds].sort().join(","),
        ].join("|");

        const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
        return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
    }
}
